import { createFileRoute, redirect } from '@tanstack/react-router'
import { AuthenticatedLayout } from '@/components/layout/authenticated-layout'
import { hasBackOfficeAccess } from '@/lib/auth'
import { supabase } from '@/lib/supabase'
import { getCachedAuth, setCachedAuth, clearAuthRouteCache } from '@/lib/auth-cache'

export const Route = createFileRoute('/_authenticated')({
  beforeLoad: async ({ location }) => {
    // getSession() may make a network call to refresh an expired token.
    // Wrap with a timeout to avoid hanging indefinitely.
    let session: Awaited<ReturnType<typeof supabase.auth.getSession>>['data']['session']
    try {
      const result = await Promise.race([
        supabase.auth.getSession(),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('session_timeout')), 5000)
        ),
      ])
      session = result.data.session
    } catch {
      session = null
    }

    const { cachedUserId, cachedUserRole } = getCachedAuth()

    if (session?.user) {
      // Session available — use cached role if same user, skip DB query
      if (cachedUserId === session.user.id && cachedUserRole) {
        return {
          user: { ...session.user, user_role: cachedUserRole },
        }
      }

      // Fallback: fetch role from DB with a timeout to avoid hanging
      let userData: { user_role: string } | null = null
      try {
        const result = await Promise.race([
          supabase.from('users').select('user_role').eq('id', session.user.id).single(),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('timeout')), 5000)
          ),
        ])
        userData = result.data
      } catch {
        // Timeout or network error — if we have cache, trust it
        if (cachedUserRole && hasBackOfficeAccess(cachedUserRole as any)) {
          return {
            user: { ...session.user, user_role: cachedUserRole },
          }
        }
        clearAuthRouteCache()
        throw redirect({
          to: '/sign-in',
          search: { redirect: location.href },
        })
      }

      if (!userData || !hasBackOfficeAccess(userData.user_role as any)) {
        clearAuthRouteCache()
        throw redirect({
          to: '/sign-in',
          search: { redirect: location.href },
        })
      }

      setCachedAuth(session.user.id, userData.user_role as any)

      return {
        user: { ...session.user, user_role: userData.user_role },
      }
    }

    // No session — but if cache exists (just set by signIn()), trust it.
    // This handles the case where getSession() is slow/fails right after login
    // in production. The Supabase session is valid but not yet available via
    // getSession() due to network latency or async storage.
    if (cachedUserId && cachedUserRole && hasBackOfficeAccess(cachedUserRole as any)) {
      return {
        user: { id: cachedUserId, user_role: cachedUserRole } as any,
      }
    }

    clearAuthRouteCache()
    throw redirect({
      to: '/sign-in',
      search: {
        redirect: location.href,
      },
    })
  },
  component: AuthenticatedLayout,
})
